import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/app/authentication/auth.service';

interface UserSession {
  id: number;
  sucursalId?: number;
  sucursalNombre?: string;
  ipAddress?: string;
  userAgent?: string;
  deviceName?: string;
  lastActivityAt?: string;
  expiresAt?: string;
  createdAt?: string;
  isCurrent?: boolean;
}

@Component({
    selector: 'app-mi-perfil',
    templateUrl: './mi-perfil.component.html',
    styleUrls: ['./mi-perfil.component.scss'],
    standalone: false
})
export class MiPerfilComponent implements OnInit {
  public guardando = false;
  public cargandoSesiones = false;
  public revocandoSesiones = false;
  public sesiones: UserSession[] = [];

  public form: FormGroup = this.fb.group({
    passwordActual: ['', [Validators.required]],
    passwordNueva: ['', [Validators.required, Validators.minLength(6)]],
    passwordConfirmacion: ['', [Validators.required]]
  });

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.cargarSesiones();
  }

  get f() {
    return this.form.controls;
  }

  guardar() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const passwordActual = String(this.f['passwordActual'].value || '');
    const passwordNueva = String(this.f['passwordNueva'].value || '');
    const passwordConfirmacion = String(this.f['passwordConfirmacion'].value || '');

    if (passwordNueva !== passwordConfirmacion) {
      this.toastr.error('La confirmación de clave no coincide.', 'Atención', {
        timeOut: 3000,
        closeButton: true,
        progressBar: true
      });
      return;
    }

    if (passwordActual === passwordNueva) {
      this.toastr.warning('La nueva clave debe ser distinta a la actual.', 'Atención', {
        timeOut: 3000,
        closeButton: true,
        progressBar: true
      });
      return;
    }

    this.guardando = true;
    this.authService.cambiarClave(passwordActual, passwordNueva).subscribe(
      () => {
        this.guardando = false;
        this.toastr.success('Clave actualizada correctamente.', 'Atención', {
          timeOut: 3000,
          closeButton: true,
          progressBar: true
        });
        this.form.reset();
        this.cargarSesiones();
      },
      (error) => {
        this.guardando = false;
        const detalle =
          error?.error?.rows?.[0]?.detalle ||
          error?.error?.error ||
          'No se pudo actualizar la clave.';
        this.toastr.error(detalle, 'Atención', {
          timeOut: 3500,
          closeButton: true,
          progressBar: true
        });
      }
    );
  }

  cargarSesiones() {
    this.cargandoSesiones = true;
    this.authService.obtenerSesionesActivas().subscribe(
      (resp: any) => {
        this.cargandoSesiones = false;
        this.sesiones = Array.isArray(resp?.rows) ? resp.rows : [];
      },
      () => {
        this.cargandoSesiones = false;
        this.sesiones = [];
        this.toastr.error('No se pudieron cargar las sesiones activas.', 'Atención', {
          timeOut: 3500,
          closeButton: true,
          progressBar: true
        });
      }
    );
  }

  revocarSesion(session: UserSession) {
    const sessionId = Number(session?.id || 0);
    if (!sessionId || this.revocandoSesiones) return;

    const confirmed = window.confirm('¿Deseas cerrar esta sesión?');
    if (!confirmed) return;

    this.revocandoSesiones = true;
    this.authService.revocarSesion(sessionId).subscribe(
      (resp: any) => {
        this.revocandoSesiones = false;
        const revokedCurrent = Boolean(resp?.rows?.[0]?.revokedCurrent);

        if (revokedCurrent) {
          this.toastr.info('Tu sesión fue cerrada. Debes iniciar sesión nuevamente.', 'Atención', {
            timeOut: 2500,
            closeButton: true,
            progressBar: true
          });
          this.authService.logout();
          return;
        }

        this.toastr.success('Sesión revocada correctamente.', 'Atención', {
          timeOut: 2500,
          closeButton: true,
          progressBar: true
        });
        this.cargarSesiones();
      },
      (error) => {
        this.revocandoSesiones = false;
        const detalle =
          error?.error?.rows?.[0]?.detalle ||
          error?.error?.error ||
          'No se pudo revocar la sesión.';
        this.toastr.error(detalle, 'Atención', {
          timeOut: 3500,
          closeButton: true,
          progressBar: true
        });
      }
    );
  }

  revocarOtrasSesiones() {
    if (this.revocandoSesiones) return;
    const confirmed = window.confirm('¿Deseas cerrar todas las demás sesiones abiertas?');
    if (!confirmed) return;

    this.revocandoSesiones = true;
    this.authService.revocarOtrasSesiones().subscribe(
      (resp: any) => {
        this.revocandoSesiones = false;
        const total = Number(resp?.rows?.[0]?.total || 0);
        this.toastr.success(`Se cerraron ${total} sesión(es).`, 'Atención', {
          timeOut: 2500,
          closeButton: true,
          progressBar: true
        });
        this.cargarSesiones();
      },
      (error) => {
        this.revocandoSesiones = false;
        const detalle =
          error?.error?.rows?.[0]?.detalle ||
          error?.error?.error ||
          'No se pudieron revocar las sesiones.';
        this.toastr.error(detalle, 'Atención', {
          timeOut: 3500,
          closeButton: true,
          progressBar: true
        });
      }
    );
  }
}
